import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  shallowEqual,
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';

import { v4 as uuidv4 } from 'uuid';

import { RootState } from '../../../../app/rootReducer';
import { Decision, DecisionType } from '../../../../common/constants/decision';
import { ProductFactory } from '../../../../common/productConfig/productFactory';
import { SnackBarType } from '../../../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../../../layout/SnackbarContext';
import { IDecision } from '../../interfaces/IDecision';
import { IRiderInfo } from '../../interfaces/IRiderInfo';
import EditIneligible from './EditIneligible';
import { ReviewIneligible } from './ReviewIneligible';
import { IneligibleFormData } from './useIneligibleForm';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const IneligibleDrawer = ({
  isOpen,
  setIsOpen,
  applicationId,
  editedDecision,
  onDecisionChanged,
  isEditLeadingToCLR,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  applicationId: string;
  editedDecision?: IDecision;
  onDecisionChanged?: (decision: Decision) => void;
  isEditLeadingToCLR?: boolean;
}) => {
  const { openSnack } = useContext(SnackbarContext);

  const {
    createDecisionFailed,
    createDecisionError,
    createDecisionErrorType,
    decisions,
    product,
    carrier,
    subCategory,
    stateAtSubmission,
  }: {
    createDecisionFailed: boolean;
    createDecisionError: string;
    createDecisionErrorType: string;
    decisions: IDecision[];
    product: string;
    carrier: string;
    subCategory: string;
    stateAtSubmission: string;
  } = useSelector(
    (state: RootState) => ({
      createDecisionFailed: state.decision.createDecisionFailed,
      createDecisionError: state.decision.createDecisionError,
      createDecisionErrorType: state.decision.createDecisionErrorType,
      decisions: state.decision.decisions,
      product: state.application.product,
      carrier: state.application.carrier,
      subCategory: state.application.subCategory,
      stateAtSubmission: state.application.stateAtSubmission,
    }),
    shallowEqual,
  );
  const appliedForDecision: IDecision | undefined = decisions?.find(
    d => d.decisionType === DecisionType.AF,
  );

  const productConfig = ProductFactory.getProductConfig(
    carrier,
    product,
    subCategory,
  );
  const [formData, setFormData] = useState<IneligibleFormData | null>(null);
  const [isReview, setIsReview] = useState(false);
  const [riders, setRiders] = useState([] as IRiderInfo[]);

  const onEditSuccess = useCallback((data: IneligibleFormData) => {
    setFormData(data);
    setIsReview(true);
  }, []);

  useEffect(() => {
    if (isOpen) return;
    setFormData(null);
  }, [isOpen]);

  useEffect(() => {
    const filteredDecisions = [...(decisions || [])].filter(
      d => d.decisionType !== DecisionType.AF,
    );
    const latestDecision: IDecision | undefined = filteredDecisions.length
      ? filteredDecisions.sort((a, b) => b.id - a.id)[0]
      : appliedForDecision;
    initRiders(latestDecision);
  }, [decisions, appliedForDecision]);

  useEffect(() => {
    if (!createDecisionFailed) return;

    openSnack({
      id: uuidv4(),
      type: createDecisionErrorType as SnackBarType,
      message: createDecisionError,
    });
  }, [createDecisionFailed, createDecisionError, createDecisionErrorType]);

  const initRiders = (decision: IDecision | undefined) => {
    if (!decision) {
      return;
    }
    if (decision.ridersInfo && decision.ridersInfo.length > 0) {
      setRiders(decision.ridersInfo);
      return;
    }

    const allRiders: string[] = appliedForDecision?.eligibleRiders || [];

    // only include ineligible riders that are also a part of applied riders, cause engine can mark other riders too.
    const ineligibleRiders: string[] = (
      decision?.ineligibleRiders || []
    ).filter(r => allRiders.includes(r));
    const eligibleRiders: string[] = allRiders.filter(
      r => !ineligibleRiders.includes(r),
    );

    setRiders(
      [
        ...eligibleRiders.map(rider => ({
          rider,
          isEligible: true,
          reason: '',
          isUnderwritable:
            productConfig
              ?.getUnderWritableRiders(stateAtSubmission)
              .includes(rider) || false,
        })),
        ...ineligibleRiders.map(rider => ({
          rider,
          isEligible: false,
          reason: '',
          isUnderwritable:
            productConfig
              ?.getUnderWritableRiders(stateAtSubmission)
              .includes(rider) || false,
        })),
      ].sort((a, b) => a.rider.localeCompare(b.rider)),
    );
  };

  return (
    <>
      {isReview && formData && (
        <ReviewIneligible
          applicationId={applicationId}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          setIsReview={setIsReview}
          editedDecision={editedDecision}
          riders={riders}
          formData={formData}
          isEditLeadingToCLR={isEditLeadingToCLR}
        />
      )}
      {!isReview && (
        <EditIneligible
          editedDecision={editedDecision}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          isUpdate={!!editedDecision}
          onDecisionChanged={onDecisionChanged}
          onSuccess={onEditSuccess}
          initialFormData={formData}
        />
      )}
    </>
  );
};
